import { mapState, mapGetters } from "vuex";
import { MOBILE_BOX_CONTROLS_MODES, PAGES, PAGE_GROUPS } from "@consts";
import {
	getSearchParamsFromRoute,
	hasDictionaryScoreField,
	isEmpty,
} from "@utils";

export default {
	...mapState("query", {
		results: (state) => state.cache.results,
		savedDictionary: (state) => state.cache.dictionary,
		totalResultsCount: (state) => state.cache.totalResultsCount,
		totalAlternativeResultsCount: (state) =>
			state.cache.totalAlternativeResultsCount,
		alternativeResults: (state) => state.cache.alternativeResults,
	}),
	...mapGetters("navigation", [
		"isUnscramblerResultsPage",
		"isWordleResultsPage",
		"isQuordleResultsPage",
		"isScrabbleDictionaryResultsPage",
		"isAnagramResultsPage",
		"isNYTCrossplayDictionaryResultsPage",
	]),
	dictionary() {
		return this.$store.getters["query/currentDictionary"];
	},
	currentPageGroup() {
		switch (this.$route.name) {
			case PAGES.WORDS_HAVING_RESULTS:
			case PAGES.WORDS_ENDING_RESULTS:
			case PAGES.WORDS_STARTING_RESULTS:
				return PAGE_GROUPS.LETTERS_PAGE;
			case PAGES.WORDS_BY_LENGTH_RESULTS:
				return PAGE_GROUPS.LENGTH_PAGE;
			case PAGES.WORDS_CONSONANTS:
			case PAGES.WORDS_VOWELS:
			case PAGES.WORDS_WITH_Q_NO_U:
			case PAGES.WORDS_ENDING_ING:
				return PAGE_GROUPS.SPECIALS_PAGE;
			case PAGES.UNSCRAMBLER_RESULTS:
				return PAGE_GROUPS.UNSCRAMBLE_PAGE;
			case PAGES.SCRABBLE_DICT_RESULTS:
				return PAGE_GROUPS.SCRABBLE_PAGE;
			case PAGES.ANAGRAM_RESULTS:
				return PAGE_GROUPS.ANAGRAM_PAGE;
			case PAGES.WORDLE_RESULTS:
			case PAGES.WORDLE:
				return PAGE_GROUPS.WORDLE_PAGE;
			case PAGES.QUORDLE_RESULTS:
			case PAGES.QUORDLE:
				return PAGE_GROUPS.QUORDLE_PAGE;
		}
	},
	wordsCount() {
		if (
			[
				PAGE_GROUPS.LETTERS_PAGE,
				PAGE_GROUPS.LENGTH_PAGE,
				PAGE_GROUPS.SPECIALS_PAGE,
			].includes(this.currentPageGroup)
		) {
			return {
				initial: 25,
				second: 50,
			};
		}
		return {
			initial: 10,
			second: 35,
		};
	},
	mobileHeaderStyle() {
		if (!this.isSmallDevice) {
			return null;
		}

		return {
			top: `${this.$store.getters["window/headerClearanceHeight"]}px`,
		};
	},
	showWordleCTA() {
		return (
			(this.searchParams?.advanced?.length === 5 ||
				this.isWordleResultsPage) &&
			this.hasResults
		);
	},
	showMobileBoxControls() {
		return this.mobileBoxControlsMode !== null;
	},
	isMobileBoxControlsFullHeight() {
		return this.mobileBoxControlsMode === MOBILE_BOX_CONTROLS_MODES.GAMES;
	},
	mobileBoxControlsTitle() {
		if (!this.showMobileBoxControls) {
			return null;
		}

		switch (this.mobileBoxControlsMode) {
			case MOBILE_BOX_CONTROLS_MODES.GAMES:
				return this.$t("UnscrambleFilterScreenH1");
			case MOBILE_BOX_CONTROLS_MODES.SEARCH:
				if (this.isWordleResultsPage) {
					return "WORDLE SOLVER";
				}
				if (this.isQuordleResultsPage) {
					return "QUORDLE SOLVER";
				}
				return this.$t("UnscrambleSearchScreenAndModuleH1");
		}

		return null;
	},
	mobileBoxControlsComponent() {
		if (!this.showMobileBoxControls) {
			return null;
		}

		switch (this.mobileBoxControlsMode) {
			case MOBILE_BOX_CONTROLS_MODES.GAMES:
				return "GameSelect";
			case MOBILE_BOX_CONTROLS_MODES.SEARCH:
				return "Finder";
		}

		return null;
	},
	showGameSelect() {
		if (
			this.currentPageGroup === PAGE_GROUPS.WORDLE_PAGE ||
			this.currentPageGroup === PAGE_GROUPS.QUORDLE_PAGE ||
			this.currentPageGroup === PAGE_GROUPS.ANAGRAM_PAGE ||
			this.$route.name === PAGES.NYT_CROSSPLAY_DICTIONARY_RESULTS
		) {
			return false;
		}
		// 	if (this.isAnagramResultsPage) {
		// 		return false;
		// 	}

		// 	if (this.isScrabbleDictionaryResultsPage && this.isSmallDevice) {
		// 		return false;
		// 	}

		return true;
	},
	// We dont need this anymoar, TODO delete. this will be handled by the module approach
	finderTitle() {
		if (this.isScrabbleDictionaryResultsPage) {
			return this.$t("ScrabbleDictionaryResultsH1");
		}

		if (this.isAnagramResultsPage) {
			return "Anagram Solver";
		}

		if (this.$route.name === PAGES.WORDLE_RESULTS) {
			return this.$t("Wordle Solver").toUpperCase();
		}

		if (this.$route.name === PAGES.QUORDLE_RESULTS) {
			return this.$t("Quordle Solver").toUpperCase();
		}

		if (this.$route.name === PAGES.NYT_CROSSPLAY_DICTIONARY_RESULTS) {
			return "NYT Crossplay Dictionary";
		}

		return this.$t("UnscrambleSearchScreenAndModuleH1");
	},
	// We dont need this anymoar, TODO delete. this will be handled by the module approach
	// isAnagramResultsPage() {
	// 	return this.$route.name === PAGES.ANAGRAM_RESULTS;
	// },
	sidebarStyle() {
		if (this.isSmallDevice) {
			return null;
		}

		const headerClearanceHeight =
			this.$store.getters["window/headerClearanceHeight"];

		return {
			top: `${headerClearanceHeight + 15}px`,
		};
	},
	hasScoreField() {
		return hasDictionaryScoreField(this.dictionary.value);
	},
	tableFields() {
		const fields = ["word"];

		return !this.hasScoreField ? fields : [...fields, "score"];
	},
	hasResults() {
		return this.totalResultsCount !== 0;
	},
	hasWordResults() {
		return !isEmpty(this.results);
	},
	hasAlternativeResults() {
		return this.totalAlternativeResultsCount !== 0;
	},
	showPlayableCard() {
		// We never want to show it on a loading state.
		if (this.loading) {
			return false;
		}

		// It always shows on a Scrabble dictionary results page.
		if (
			this.isScrabbleDictionaryResultsPage ||
			this.isNYTCrossplayDictionaryResultsPage
		) {
			return true;
		}

		return (
			!isEmpty(this.$store.state.query?.cache?.match) &&
			this.hasScoreField
		);
	},
	showScrabbleDictionaryFirstInContentAd() {
		return (
			this.isSmallOrMediumPortraitDevice &&
			this.isScrabbleDictionaryResultsPage
		);
	},
	secondHeading() {
		const userInput = this.$route.params.tiles.toUpperCase();

		if (this.$route.name === PAGES.NYT_CROSSPLAY_DICTIONARY_RESULTS) {
			return `Other Crossplay Words with <strong>${userInput}</strong>`;
		}

		return this.$t("ScrabbleDictionaryResultsH3OtherWords", {
			UserQuery: `<strong>${userInput}</strong>`,
		});
	},
	anagramWords() {
		return this.$store.state.query.cache.anagrams;
	},
	anagramTablesHint() {
		if (this.hasAnagrams) {
			return `The full anagram result${
				!this.hasOneAnagram ? "s" : ""
			} above ${!this.hasOneAnagram ? "are" : "is a"} true anagram${
				!this.hasOneAnagram ? "s" : ""
			}, using every letter exactly once to create a new word. Listed below are partial anagrams, which simply unscramble letters from the original word to form new words of any length.`;
		}

		return !this.hasResults
			? "You’re close! While there are no exact matches, modifying your search can help you get more results."
			: "Not every word can be rearranged to form a true anagram. In this case, there are no real anagrams for your original word. But, don’t fret! The partial anagrams below consist of words of any length you can create by unscrambling your letters.";
	},
	anagramsTableCopy() {
		if (this.hasAnagrams) {
			return "More anagram options";
		}

		return !this.hasResults ? "" : "More anagram options";
	},
	anagramsTotal() {
		return this.anagramWords?.[this.anagramWordsKey]?._items?.length || 0;
	},
	hasAnagrams() {
		return this.anagramWords[this.anagramWordsKey]?._items;
	},
	anagramWordsKey() {
		return Object.keys(this.anagramWords);
	},
	hasOneAnagram() {
		return this.anagramsTotal === 1;
	},
	usesPillsLayout() {
		const params = getSearchParamsFromRoute(this.$route);

		// If it's not a length page or a length combination page, return false.
		if (
			![
				PAGES.WORDS_BY_LENGTH_RESULTS,
				PAGES.WORDS_COMBINATION_LENGTH_AND_LETTERS_RESULTS,
			].includes(this.$route.name)
		) {
			return false;
		}

		//
		return [4, 6, 7].includes(params.clean?.advanced?.length);
	},
	pillsLayoutAdIntervals() {
		if (!this.usesPillsLayout) {
			return null;
		}

		const params = getSearchParamsFromRoute(this.$route);
		const length = params.clean?.advanced?.length;

		switch (length) {
			case 4:
				return {
					first: 15,
					next: 35,
				};
			case 7:
				return {
					first: 12,
					next: 36,
				};
			default:
				return {
					first: 13,
					next: 37,
				};
		}
	},
};
